import {
  Button,
  HStack,
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm, UseFormRegisterReturn } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { emailRegex, phoneRegex } from '@duke-hq/shared-constants/regexPatterns';
import { ChevronRightIcon } from 'lucide-react';
import { djs } from '@duke-hq/djs';
import { STUDENT_MAXIMUM_AGE, STUDENT_MINIMUM_AGE } from '@duke-hq/shared-constants/students';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { quickReferralModalState } from '../../tenantState/QuickReferralState';
import { BasicInfoDataTypes } from './types';
import { useSnapshot } from 'valtio';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useCallback, useRef } from 'react';

const USI_REGEX = /^[A-Za-z0-9]{10}$/;

const disabledInputStyles = {
  opacity: 0.4,
  cursor: 'not-allowed',
  bg: 'bg.subtle',
  color: 'fg.muted',
  borderColor: 'bg.muted',
  _hover: {
    bg: 'bg.subtle',
    borderColor: 'bg.muted',
  },
};

type BasicInfoProps = {
  goToNextStep: () => void;
};

export function BasicInfo({ goToNextStep }: BasicInfoProps) {
  const quickReferralModalSnap = useSnapshot(quickReferralModalState);
  const toast = useToast();

  const lastCheckedData = useRef({
    email: quickReferralModalSnap.basicInfoData.email ?? '',
    phoneNumber: quickReferralModalSnap.basicInfoData.phone_number ?? '',
  });

  const lastCheckedUsi = useRef(
    quickReferralModalSnap.basicInfoData.universal_student_identifier ?? '',
  );

  const checkUSIUniqueness = trpcTenant.users.checkUSIUniqueness.useMutation({
    onSuccess(result) {
      quickReferralModalState.usiConflict = !result.isUnique;
    },
    onError(error) {
      toast({
        title: t`USI verification failed`,
        description: error.message,
        status: 'error',
        duration: 7000,
      });
    },
  });

  const checkStudentExists = trpcTenant.users.checkStudentReEnrolmentEligibility.useMutation({
    onSuccess(result) {
      if (result.isNewStudent) {
        quickReferralModalState.existingFields = null;
        quickReferralModalState.existingStudentId = null;
        quickReferralModalState.hasActiveAccreditedCourse = false;
      } else {
        quickReferralModalState.existingFields = result.existingFields ?? null;
        quickReferralModalState.existingStudentId = result.existingStudentId;
        quickReferralModalState.hasActiveAccreditedCourse = result.hasActiveAccreditedCourse;
      }

      // Re-validate USI with updated existingStudentId when there's an active conflict
      if (quickReferralModalState.usiConflict) {
        const usi = getValues('universal_student_identifier');
        const normalisedUsi = typeof usi === 'string' ? usi.trim().toUpperCase() : '';
        if (USI_REGEX.test(normalisedUsi)) {
          lastCheckedUsi.current = normalisedUsi;
          checkUSIUniqueness.mutate({
            usi: normalisedUsi,
            existingStudentId: result.isNewStudent
              ? undefined
              : (result.existingStudentId ?? undefined),
          });
        }
      }
    },
    onError(error) {
      toast({
        title: t`Participant details verification failed`,
        description: error.message,
        status: 'error',
        duration: 7000,
      });
    },
  });

  const {
    handleSubmit,
    register,
    getValues,
    reset,
    setFocus,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<BasicInfoDataTypes>({
    mode: 'onChange',
    defaultValues: {
      first_name: quickReferralModalSnap.basicInfoData.first_name,
      last_name: quickReferralModalSnap.basicInfoData.last_name,
      email: quickReferralModalSnap.basicInfoData.email,
      phone_number: quickReferralModalSnap.basicInfoData.phone_number,
      date_of_birth: quickReferralModalSnap.basicInfoData.date_of_birth
        ? djs(quickReferralModalSnap.basicInfoData.date_of_birth).format('YYYY-MM-DD')
        : '',
      universal_student_identifier:
        quickReferralModalSnap.basicInfoData.universal_student_identifier,
    },
  });

  // Watch form values to detect when validation is needed
  const watchedEmail = watch('email');
  const watchedPhoneNumber = watch('phone_number');
  const watchedUsi = watch('universal_student_identifier');

  // Determine if validation is needed
  const needsValidation =
    isValid &&
    !checkStudentExists.isPending &&
    watchedEmail !== '' &&
    (watchedEmail !== lastCheckedData.current.email ||
      watchedPhoneNumber !== lastCheckedData.current.phoneNumber);

  const needsUsiValidation =
    !checkUSIUniqueness.isPending &&
    typeof watchedUsi === 'string' &&
    USI_REGEX.test(watchedUsi) &&
    watchedUsi.toUpperCase() !== lastCheckedUsi.current;

  const checkIfShouldValidateStudent = useDebouncedCallback(() => {
    if (!isValid) {
      return;
    }

    const email = getValues('email');
    const phoneNumber = getValues('phone_number');

    // Check if email or phoneNumber has changed since last check
    if (
      email &&
      (email !== lastCheckedData.current.email ||
        phoneNumber !== lastCheckedData.current.phoneNumber)
    ) {
      lastCheckedData.current = { email, phoneNumber };
      checkStudentExists.mutate({ email, phoneNumber });
    }
  }, 500);

  const checkIfShouldValidateUsi = useDebouncedCallback(() => {
    const usi = getValues('universal_student_identifier');
    const normalisedUsi = typeof usi === 'string' ? usi.trim().toUpperCase() : '';

    if (!USI_REGEX.test(normalisedUsi)) {
      quickReferralModalState.usiConflict = false;
      lastCheckedUsi.current = '';
      return;
    }

    if (normalisedUsi !== lastCheckedUsi.current) {
      lastCheckedUsi.current = normalisedUsi;
      checkUSIUniqueness.mutate({
        usi: normalisedUsi,
        existingStudentId: quickReferralModalSnap.existingStudentId ?? undefined,
      });
    }
  }, 500);

  const withValidityCheck = (registration: UseFormRegisterReturn<string>) => ({
    ...registration,
    onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
      await registration.onChange(e);
      checkIfShouldValidateStudent();
    },
  });

  const withUsiValidation = (registration: UseFormRegisterReturn<string>) => ({
    ...registration,
    onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
      await registration.onChange(e);
      quickReferralModalState.usiConflict = false;
      checkIfShouldValidateUsi();
    },
  });

  function onSubmit(values: BasicInfoDataTypes) {
    const { date_of_birth } = values;
    if (date_of_birth == null || date_of_birth == '') {
      values.date_of_birth = null;
    } else {
      values.date_of_birth = new Date(date_of_birth);
    }

    quickReferralModalState.setBasicInfoData(values);
    goToNextStep();
  }

  const handleClearForm = useCallback(() => {
    reset({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date_of_birth: '',
      universal_student_identifier: '',
    });
    lastCheckedData.current = { email: '', phoneNumber: '' };
    lastCheckedUsi.current = '';
    quickReferralModalState.resetStates();
    requestAnimationFrame(() => setFocus('first_name')); // Focus after reset re-render completes
  }, [reset, setFocus]);

  const today = djs();
  const maxDate = today.subtract(STUDENT_MINIMUM_AGE, 'year').format('YYYY-MM-DD');
  const minDate = today.subtract(STUDENT_MAXIMUM_AGE, 'year').format('YYYY-MM-DD');

  const existingFields = quickReferralModalSnap.existingFields;
  const isDobDisabled =
    existingFields?.dateOfBirth === true || existingFields?.usiVerified === true;
  const isUsiDisabled =
    existingFields?.universalStudentIdentifier === true || existingFields?.usiVerified === true;

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', height: '100%' }} noValidate>
      <FormMainContainer>
        <FormContentContainer>
          <FormControl isInvalid={!!errors.first_name} isRequired>
            <FormLabel htmlFor="first_name">
              <Trans>First name</Trans>
            </FormLabel>
            <Input
              id="first_name"
              {...withValidityCheck(
                register('first_name', {
                  required: t`First name is required`,
                  setValueAs: (value) => (typeof value === 'string' ? value.trim() : ''),
                }),
              )}
              type="text"
              spellCheck="false"
              autoComplete="off"
              isDisabled={existingFields?.firstName === true}
              sx={existingFields?.firstName === true ? disabledInputStyles : undefined}
            />
            <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.last_name} isRequired>
            <FormLabel htmlFor="last_name">
              <Trans>Last name</Trans>
            </FormLabel>
            <Input
              id="last_name"
              {...withValidityCheck(
                register('last_name', {
                  required: t`Last name is required`,
                  setValueAs: (value) => (typeof value === 'string' ? value.trim() : ''),
                }),
              )}
              type="text"
              spellCheck="false"
              autoComplete="off"
              isDisabled={existingFields?.lastName === true}
              sx={existingFields?.lastName === true ? disabledInputStyles : undefined}
            />
            <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
          </FormControl>

          <HStack w="full" justify="space-between" align="start" gap="10px">
            <FormControl isInvalid={!!errors.email} isRequired>
              <FormLabel htmlFor="email">
                <Trans>Email address</Trans>
              </FormLabel>
              <Input
                id="email"
                {...withValidityCheck(
                  register('email', {
                    required: t`Email address is required`,
                    pattern: {
                      value: emailRegex,
                      message: t`Please enter a valid email address`,
                    },
                    setValueAs: (value) => (typeof value === 'string' ? value.trim() : ''),
                  }),
                )}
                type="email"
                spellCheck="false"
                autoComplete="off"
                isDisabled={existingFields?.email === true}
                sx={existingFields?.email === true ? disabledInputStyles : undefined}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.phone_number} isRequired>
              <FormLabel htmlFor="phone_number">
                <Trans>Phone number</Trans>
              </FormLabel>
              <Input
                id="phone_number"
                {...withValidityCheck(
                  register('phone_number', {
                    required: t`Phone number is required`,
                    pattern: {
                      value: phoneRegex,
                      message: t`Please enter a valid phone number`,
                    },
                    setValueAs: (value) =>
                      typeof value === 'string' ? value.replace(/\s/g, '') : '',
                  }),
                )}
                type="tel"
                spellCheck="false"
                autoComplete="off"
                isDisabled={existingFields?.phoneNumber === true}
                sx={existingFields?.phoneNumber === true ? disabledInputStyles : undefined}
              />
              <FormErrorMessage>{errors.phone_number?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.date_of_birth}>
            <FormLabel htmlFor="date_of_birth">
              <Trans>Date of birth</Trans>
            </FormLabel>
            <Input
              id="date_of_birth"
              min={minDate}
              max={maxDate}
              {...register('date_of_birth', {
                min: {
                  value: minDate,
                  message: t`Please enter a valid date of birth. Age must be ${STUDENT_MINIMUM_AGE}-${STUDENT_MAXIMUM_AGE} years.`,
                },
                max: {
                  value: maxDate,
                  message: t`Please enter a valid date of birth. Age must be ${STUDENT_MINIMUM_AGE}-${STUDENT_MAXIMUM_AGE} years.`,
                },
                validate: (value) => {
                  if (value == null || value == '') {
                    return true;
                  }
                  return djs(value).isValid() || t`Please enter a valid date`;
                },
              })}
              type="date"
              defaultValue={djs(quickReferralModalSnap.basicInfoData.date_of_birth).format(
                'YYYY-MM-DD',
              )}
              isDisabled={isDobDisabled}
              sx={isDobDisabled ? disabledInputStyles : undefined}
            />
            <FormErrorMessage>{errors.date_of_birth?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.universal_student_identifier}>
            <FormLabel htmlFor="universal_student_identifier">
              <Trans>USI (if known)</Trans>
            </FormLabel>
            <Input
              id="universal_student_identifier"
              type="text"
              spellCheck="false"
              autoComplete="off"
              {...withUsiValidation(
                register('universal_student_identifier', {
                  pattern: {
                    value: USI_REGEX,
                    message: t`Universal Student Identifier must be exactly 10 letters or numbers`,
                  },
                  setValueAs: (value) =>
                    typeof value === 'string' ? value.trim().toUpperCase() : '',
                }),
              )}
              defaultValue={quickReferralModalSnap.basicInfoData.universal_student_identifier ?? ''}
              textTransform="uppercase"
              isDisabled={isUsiDisabled}
              sx={isUsiDisabled ? disabledInputStyles : undefined}
            />
            <FormErrorMessage>{errors.universal_student_identifier?.message}</FormErrorMessage>
          </FormControl>

          {quickReferralModalSnap.usiConflict ? (
            <Alert status="warning" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">
                <Trans>
                  This USI is already associated with another student. Please check the USI and try
                  again, or contact support for assistance.
                </Trans>
              </Text>
            </Alert>
          ) : null}

          {existingFields ? (
            <VStack spacing={2} mt={4} w="full">
              <Alert status="info" borderRadius="md" variant="subtle">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="sm">
                    <Trans>Student account already exists</Trans>
                  </Text>
                  <Text fontSize="sm">
                    <Trans>
                      An account using this email address or phone number exists on our system. No
                      further information is required.
                    </Trans>
                  </Text>
                </VStack>
              </Alert>
              <HStack fontSize="xs" w="full">
                <Text>
                  <Trans>Do you want to enrol a different participant?</Trans>
                </Text>
                <Button variant="link" onClick={handleClearForm} size="xs">
                  <Trans>Clear form</Trans>
                </Button>
              </HStack>
            </VStack>
          ) : null}
        </FormContentContainer>

        <FormFooterContainer>
          <Button
            rightIcon={<ChevronRightIcon />}
            isLoading={
              isSubmitting ||
              checkStudentExists.isPending ||
              needsValidation ||
              checkUSIUniqueness.isPending ||
              needsUsiValidation
            }
            type="submit"
            isDisabled={
              !isValid ||
              checkStudentExists.isPending ||
              needsValidation ||
              checkUSIUniqueness.isPending ||
              needsUsiValidation ||
              quickReferralModalSnap.usiConflict
            }
          >
            <Trans>Next</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </form>
  );
}
